import { Component, HostListener } from "@angular/core";
import { ColDef, ExcelStyle, GridApi, GridOptions, GridReadyEvent } from "ag-grid-community";
import { ToastrService } from "ngx-toastr";
import { PfRequest } from "src/app/generated-models/PfRequest";
import { PfSubRequest } from "src/app/generated-models/PfSubRequest";
import { ApiService } from "src/app/services";
import { SharedDataService } from "src/app/services/sharedDataService";
import { LoadingScreenService } from "src/app/shared/loading-screen.service";
import { ModalService } from "src/app/shared/modal.service";
import { LinkCellComponent } from "src/app/shared/grid/link-cell.component";
import { GridViewBaseComponent } from "src/app/utility/grid-view-base.component";
import { getExportedExcelFileNameSuffix } from "src/app/utility/common-helper";
import { configurePfSubRequestExpansion } from "./pf-subrequest-grid";

interface ApprovablePfRequestGridRow {
  id: number;
  requestId: string;
  serviceTreeId: string;
  serviceTeam: string;
  region: string;
  purpose: string;
  manatree: string;
  status: string;
  createdTime: Date;
  createdBy: string;
  subRequests: PfSubRequest[];
}

@Component({
  standalone: false,
  templateUrl: "./pf-ad-hoc-requests-approvable.component.html",
  styleUrls: ["../../styles.scss", "./pf.scss"],
})
export class PfAdHocRequestsApprovableComponent extends GridViewBaseComponent {
  gridApi: GridApi;
  gridOptions: GridOptions;
  initPageSize = 15;
  rowData: ApprovablePfRequestGridRow[] = [];
  allRequestRows: ApprovablePfRequestGridRow[] = [];
  selectedRowCount = 0;
  showMyServiceTeamRequestsOnly = true;
  showRejected = false;
  myServiceTreeIds: string[] = [];
  keyFilterStats = "filter:approve-pf-ad-hoc-requests";
  headerStyle: ExcelStyle[] = [
    {
      id: "header",
      font: {
        bold: true,
      },
    },
  ];

  constructor(
    protected apiService: ApiService,
    protected sharedDataService: SharedDataService,
    private readonly loadingService: LoadingScreenService,
    private readonly notificationService: ToastrService,
    private readonly modalService: ModalService
  ) {
    super(apiService, sharedDataService);
  }

  ngOnInit() {
    super.InitAgGrid(this.buildColumnDefs());
    this.gridOptions.rowSelection = "multiple";
    this.gridOptions.rowMultiSelectWithClick = true;
    this.gridOptions.suppressRowClickSelection = false;
    configurePfSubRequestExpansion(this.gridOptions);
    this.refreshData();
  }

  override onProfileLoaded() {
    this.showMyServiceTeamRequestsOnly = !(this.userProfile?.IsApprover ?? false);
    this.applyStatusFilter();
  }

  onGridReady(params: GridReadyEvent) {
    super.onGridReady(params);
  }

  async refreshData() {
    this.selectedRowCount = 0;
    this.loadingService.setLoading(true);

    try {
      const [requests, teams, myPfPlans] = await Promise.all([
        this.apiService.getAllPfRequests().toPromise(),
        this.apiService.getTeamList().toPromise(),
        this.apiService.getMyPfPlans().toPromise(),
      ]);
      this.myServiceTreeIds = (myPfPlans ?? []).map((plan) => plan.ServiceTreeId);
      const serviceTeamNames = new Map(teams.map((team) => [team.Oid, team.Name]));
      this.allRequestRows = requests.map((request: PfRequest) => ({
        id: request.Id,
        requestId: request.Id.toString(),
        serviceTreeId: request.ServiceTreeId,
        serviceTeam: serviceTeamNames.get(request.ServiceTreeId) ?? "",
        region: request.Region,
        purpose: request.Purpose,
        manatree: request.Manatree,
        status: request.Status,
        createdTime: request.CreatedTime,
        createdBy: request.CreatedBy,
        subRequests: request.SubRequests ?? [],
      }));
      this.applyStatusFilter();
      super.setupFilterByLocalData();
    } catch (_error) {
      this.allRequestRows = [];
      this.rowData = [];
      this.notificationService.error("Failed to load approvable PF requests.");
    } finally {
      this.loadingService.setLoading(false);
    }
  }

  onShowRejectedChanged() {
    this.applyStatusFilter();
  }

  onShowMyServiceTeamRequestsOnlyChanged() {
    this.applyStatusFilter();
  }

  onSelectionChanged() {
    this.selectedRowCount = this.gridApi.getSelectedRows().length;
  }

  showApprovalActions(): boolean {
    return !!this.userProfile && (this.userProfile.IsAdmin || this.userProfile.IsPfAdmin);
  }


  async approveSelectedRequests() {
    const selectedRows = this.gridApi.getSelectedRows() as ApprovablePfRequestGridRow[];
    if (selectedRows.length === 0) {
      this.notificationService.warning("Please select at least one PF request to approve.");
      return;
    }

    try {
      await this.modalService.confirmationModal(
        `You are about to <strong>approve</strong> ${this.formatSelectedRequestIds(selectedRows)}. Do you want to continue?`
      );
    } catch {
      return;
    }

    await this.processSelectedRequests(
      selectedRows,
      (request) => this.apiService.approvePfRequest(request.id).toPromise(),
      "approve",
      "approved"
    );
  }

  async rejectSelectedRequests() {
    const selectedRows = this.gridApi.getSelectedRows() as ApprovablePfRequestGridRow[];
    if (selectedRows.length === 0) {
      this.notificationService.warning("Please select at least one PF request to reject.");
      return;
    }

    try {
      await this.modalService.confirmationModal(
        `You are about to <strong>reject</strong> ${this.formatSelectedRequestIds(selectedRows)}. Do you want to continue?`
      );
    } catch {
      return;
    }

    await this.processSelectedRequests(
      selectedRows,
      (request) => this.apiService.rejectPfRequest(request.id).toPromise(),
      "reject",
      "rejected"
    );
  }

  exportExcel() {
    this.gridApi.exportDataAsExcel({
      fileName: "ApprovablePfAdHocRequests-" + getExportedExcelFileNameSuffix(),
      sheetName: "Approvable PF Ad Hoc Requests",
      columnKeys: this.gridApi.getAllGridColumns().filter((column) => column.getColDef().colId !== "action"),
    });
  }

  @HostListener("window:resize")
  onResize() {
    setTimeout(() => {
      this.gridApi?.autoSizeAllColumns();
    }, 100);
  }

  private buildColumnDefs(): ColDef[] {
    return [
      {
        headerName: "Action",
        colId: "action",
        sortable: false,
        filter: false,
        suppressMenu: true,
        checkboxSelection: true,
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        cellRenderer: LinkCellComponent,
        cellRendererParams: {
          text: () => "Review",
          target: "_blank",
          url: (params) => `/quota/pf/requests/${params.data.id}`,
        },
      },
      {
        headerName: "Request ID",
        field: "requestId",
        filter: true,
        sortable: true,
        cellRenderer: "agGroupCellRenderer",
      },
      {
        headerName: "Service Tree ID",
        field: "serviceTreeId",
        filter: true,
        sortable: true,
      },
      {
        headerName: "Service Team",
        field: "serviceTeam",
        filter: true,
        sortable: true,
      },
      {
        headerName: "Region",
        field: "region",
        filter: true,
        sortable: true,
      },
      {
        headerName: "Purpose",
        field: "purpose",
        filter: true,
        sortable: true,
      },
      {
        headerName: "Manatree",
        field: "manatree",
        filter: true,
        sortable: true,
      },
      {
        headerName: "Status",
        field: "status",
        filter: true,
        sortable: true,
      },
      {
        headerName: "Created By",
        field: "createdBy",
        filter: true,
        sortable: true,
      },
      {
        headerName: "Created Date",
        field: "createdTime",
        filter: true,
        sortable: true,
      },
    ];
  }

  private applyStatusFilter() {
    this.rowData = this.allRequestRows.filter(
      (request) => {
        const matchesStatus = request.status === "Created" || (this.showRejected && request.status === "Rejected");
        const matchesServiceTeam = !this.showMyServiceTeamRequestsOnly
          || this.myServiceTreeIds.includes(request.serviceTreeId);

        return matchesStatus && matchesServiceTeam;
      }
    );
    if (this.gridApi) {
      this.gridApi.setGridOption("rowData", this.rowData);
      this.gridApi.deselectAll();
    }
    this.selectedRowCount = 0;
  }

  private formatSelectedRequestIds(selectedRows: ApprovablePfRequestGridRow[]): string {
    const requestIds = selectedRows.map((request) => `<strong>${request.id}</strong>`).join(", ");

    return selectedRows.length === 1
      ? `PF request (id: ${requestIds})`
      : `PF requests (ids: ${requestIds})`;
  }


  private async processSelectedRequests(
    selectedRows: ApprovablePfRequestGridRow[],
    action: (request: ApprovablePfRequestGridRow) => Promise<unknown>,
    actionName: string,
    successVerb: string
  ) {
    this.loadingService.setLoading(true);

    try {
      await Promise.all(selectedRows.map(action));
      this.notificationService.success(`${selectedRows.length} PF request(s) ${successVerb} successfully.`);
      await this.refreshData();
    } catch (_error) {
      this.notificationService.error(`Failed to ${actionName} the selected PF requests.`);
    } finally {
      this.loadingService.setLoading(false);
    }
  }
}
